import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { isEqual as lodashIsEqual } from 'lodash';
import { PropertyField } from '../system/field';
import React from 'react';
import { ControlField, FieldMetadata } from '../contexts/MetadataContext';

dayjs.extend(advancedFormat);

const ACRE_TO_SQFT = 43560;

export const toUrlFromQuery = (
  endpoint: string,
  query: Record<string, any>,
) => {
  const queryString = new URLSearchParams(
    Object.entries(query).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value.toString();
        }
        return acc;
      },
      {} as Record<string, string>,
    ),
  ).toString();

  return `${endpoint}${queryString ? `?${queryString}` : ''}`;
};

export const isFieldEmpty = (key: string, fieldValue: any) => {
  if (key === PropertyField.OFFICE_EXCLUSIVE_TERMS_OF_USE) {
    return fieldValue !== 1 && fieldValue !== true;
  }
  return (
    typeof fieldValue === 'undefined' ||
    fieldValue === null ||
    `${fieldValue}`.length === 0 ||
    (Array.isArray(fieldValue) && fieldValue.length === 0)
  );
};

export const toStandardDateFormat = (
  value: Date | string | number | dayjs.Dayjs,
  format = 'MM/DD/YYYY',
): string => {
  let date: dayjs.Dayjs;

  if (dayjs.isDayjs(value)) {
    // If it's already a dayjs object, use it directly
    date = value;
  } else if (typeof value === 'string') {
    // If it's a string, attempt to parse it as a date
    date = dayjs(value);
    if (!date.isValid()) {
      throw new Error('Invalid date string provided');
    }
  } else if (value instanceof Date) {
    // If it's a Date object, convert it to dayjs
    date = dayjs(value);
  } else if (typeof value === 'number') {
    // If it's a number, treat it as a timestamp
    date = dayjs(value);
    if (!date.isValid()) {
      throw new Error('Invalid timestamp provided');
    }
  } else {
    throw new Error(
      'Invalid input: must be a Date object, dayjs object, ISO date string, or timestamp number',
    );
  }

  return date.format(format);
};

export const toFormattedPrice = (
  value: number | string | null | undefined,
  fallbackText: string = 'Not Set',
): string => {
  if (value === null || value === undefined || value === '' || value === 0) {
    return fallbackText;
  }

  let numericValue: number;

  if (typeof value === 'string') {
    // Remove any non-numeric characters (except decimal point)
    const cleanedValue = value.replace(/[^\d.]/g, '');
    numericValue = parseFloat(cleanedValue);
  } else if (typeof value === 'number') {
    numericValue = value;
  } else {
    throw new Error(
      'Invalid input: must be a number or a string representing a number',
    );
  }

  if (isNaN(numericValue)) {
    return fallbackText;
  }

  const hasDecimals = `${numericValue}`.includes('.');
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export const toSystemFormat = (
  value: Date | string | number | dayjs.Dayjs,
  includeTime: boolean = false,
): string => {
  let date: dayjs.Dayjs;

  if (dayjs.isDayjs(value)) {
    // If it's already a dayjs object, use it directly
    date = value;
  } else if (typeof value === 'string') {
    // If it's a string, attempt to parse it as a date
    date = dayjs(value);
    if (!date.isValid()) {
      throw new Error('Invalid date string provided');
    }
  } else if (value instanceof Date) {
    // If it's a Date object, convert it to dayjs
    date = dayjs(value);
  } else if (typeof value === 'number') {
    // If it's a number, treat it as a timestamp
    date = dayjs(value);
    if (!date.isValid()) {
      throw new Error('Invalid timestamp provided');
    }
  } else {
    throw new Error(
      'Invalid input: must be a Date object, dayjs object, ISO date string, or timestamp number',
    );
  }

  // Format as "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ssZ" based on includeTime parameter
  return includeTime
    ? date.format('YYYY-MM-DDTHH:mm:ssZ')
    : date.format('YYYY-MM-DD');
};

export const toFormattedTime = (
  date: Date | string | number | dayjs.Dayjs = new Date(),
  timeFormat: string = 'h:mm:ss A',
): string => {
  return dayjs(date).format('h:mm:ss A');
};

const RELATIVE_EPSILON = 1e-6; // 0.0001% difference

function areNumbersEqual(a: number, b: number): boolean {
  // If the numbers are exactly the same, return true
  if (a === b) return true;

  // If both numbers are effectively integers (no meaningful decimal part)
  const aInt = Math.abs(a - Math.floor(a)) < RELATIVE_EPSILON;
  const bInt = Math.abs(b - Math.floor(b)) < RELATIVE_EPSILON;

  if (aInt && bInt) {
    return Math.floor(a) === Math.floor(b);
  }

  // For decimals or mixed comparisons, use exact equality
  return false;
}

export function customIsEqual(a: any, b: any): boolean {
  // If both values are numbers, use our custom number comparison
  if (typeof a === 'number' && typeof b === 'number') {
    return areNumbersEqual(a, b);
  }

  // For all other types, use lodash's isEqual
  return lodashIsEqual(a, b);
}

/**
 * Takes a field value from a listing, or from a reso rule apply and turns it
 * into a list. For use in multi selecting or dropdowns.
 *
 * @param fieldValue (a string or list)
 */
export function toFieldList<T>(fieldValue: any): Array<T> {
  if (Array.isArray(fieldValue)) {
    // This field is already a list, return that list
    return fieldValue;
  }
  if (typeof fieldValue === 'string') {
    if (fieldValue.trim().length === 0) {
      // Skip a split operation -- this is an empty string
      return [];
    }
    // Older RESO rules will provide values with comma splits. Transform this
    // to a proper list
    return fieldValue.split(',') as T[];
  }

  // At this point: it's not a list, and it's not a string
  // we must throw away the field value and allow addedit to handle upstream
  return [];
}

export function parsePostalCode(postalCode: string) {
  if (!postalCode) return { base: '', plus4: '' };

  const parts = postalCode.split('-');
  return {
    base: parts[0],
    plus4: parts.length > 1 ? parts[1] : '',
  };
}

export function navigateTo(path: string | undefined, newTab: boolean = false) {
  if (newTab) {
    window.open(path, '_blank')?.focus();
  } else {
    window.location.href = `${path}`;
  }
}

// Safely extract path from URL for navigation
export const getNavigationPath = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Get everything after the domain, removing any trailing slashes
    const path = urlObj.pathname.replace(/\/+$/, '');

    // If the path is empty, default to root
    const basePath = !path || path === '' ? '/' : path;

    // Basic security check - ensure path only contains safe characters
    // This regex allows letters, numbers, hyphens, underscores, and forward slashes
    if (!/^[a-zA-Z0-9\-_/]+$/.test(basePath)) {
      console.warn('Invalid characters in navigation path, defaulting to home');
      return '/';
    }

    // Preserve query parameters
    return urlObj.search ? `${basePath}${urlObj.search}` : basePath;
  } catch (error) {
    console.warn('Invalid URL format, defaulting to home', error);
    return '/';
  }
};

export const toHTTPSURL = (url?: string): string => {
  if (!url) {
    return '';
  }

  const normalizedURL = url.toLowerCase();

  if (normalizedURL.startsWith('https://')) {
    return url;
  }

  if (normalizedURL.startsWith('http://')) {
    return `https://${url.substring(7)}`;
  }

  return '';
};

export const formatPhone = (value: string) => {
  if (!value) return value;
  const phoneValue = value.replace(/[^\d]/g, '');
  const phoneLength = phoneValue.length;

  if (phoneLength < 4) {
    return phoneValue;
  } else if (phoneLength < 7) {
    return `(${phoneValue.slice(0, 3)}) ${phoneValue.slice(3)}`;
  }
  const fullPhone = `(${phoneValue.slice(0, 3)}) ${phoneValue.slice(3, 6)}-${phoneValue.slice(6, 10)}`;
  return fullPhone.slice(0, 14);
};

export const isWithinBusinessHours = (
  timeStr: string,
  isEndTimeCheck: boolean,
  timezone: string,
): boolean => {
  const time = dayjs(timeStr).tz(timezone);
  const hour = time.hour();
  const minute = time.minute();

  // Convert current time to minutes since midnight
  const currentTimeInMinutes = hour * 60 + minute;

  // Define business hours in minutes
  const businessStartMinutes = 8 * 60; // 8:00 AM
  const businessEndMinutes = 20 * 60; // 8:00 PM

  if (isEndTimeCheck) {
    // For end time checks:
    // Must be > business start time (8 AM not valid as end time)
    // Must be <= business end time (8 PM is valid as end time)
    return (
      currentTimeInMinutes > businessStartMinutes &&
      currentTimeInMinutes <= businessEndMinutes
    );
  } else {
    // For start time checks:
    // Must be >= business start time (8 AM is valid as start time)
    // Must be < business end time (8 PM not valid as start time)
    return (
      currentTimeInMinutes >= businessStartMinutes &&
      currentTimeInMinutes < businessEndMinutes
    );
  }
};

export const isValidNumber = (value: string): boolean => {
  return (
    value !== '' &&
    value !== null &&
    !isNaN(parseFloat(value)) &&
    !`${value}`.endsWith('.')
  );
};

// Make number strings in to pretty comma displayed values: 1000 -> 1,000
export const formatNumberWithCommas = (value: string | number) => {
  const valueStr = value.toString();
  return valueStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Make number strings format wih desired precision (no extra decimal places beyond provided)
export const formatNumberWithPrecision = (
  value: string | number,
  precision: number = 2,
) => {
  const numValue = parseFloat(value.toString());
  if (isNaN(numValue)) return '';

  // Format with up to {precision) decimal places, remove trailing zeros
  return numValue.toFixed(precision).replace(/\.?0+$/, '');
};

export const calculateSqFt = (acres: number) => {
  return Math.round(acres * ACRE_TO_SQFT);
};

// Add these functions to your utils.ts file

/**
 * Formats a number with commas and always shows two decimal places
 * Example: 1234.5 becomes "1,234.50"
 */
export const formatNumberWithCommasAndDecimals = (
  value: number | string,
): string => {
  if (value === null || value === undefined || value === '') return '';

  // Convert to number first to handle rounding
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';

  // Format to always show 2 decimal places
  const fixedValue = numValue.toFixed(2);

  // Split by decimal point
  const parts = fixedValue.split('.');

  // Format the integer part with commas
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Join back with decimal part
  return `${parts[0]}.${parts[1]}`;
};

/**
 * Validates if a string is a valid decimal number with up to maxDecimals decimal places
 */
export const isValidDecimalInput = (
  input: string,
  maxDecimals: number = 12,
): boolean => {
  const regex = new RegExp(`^(\\d*\\.?\\d{0,${maxDecimals}})$`);
  return regex.test(input);
};

/**
 * Handles keydown events for numeric input fields
 * Allows navigation keys, numbers, and conditionally decimal points
 */
export const handleNumericKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  currentValue: string,
  allowDecimals: boolean = true,
): void => {
  // Allow keyboard shortcuts (Ctrl+A, Ctrl+V, Ctrl+C, etc.)
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  // Allow: backspace, delete, tab, escape, enter, arrow keys
  if (
    e.key === 'Backspace' ||
    e.key === 'Delete' ||
    e.key === 'Tab' ||
    e.key === 'Escape' ||
    e.key === 'Enter' ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowRight' ||
    e.key === 'ArrowUp' ||
    e.key === 'ArrowDown' ||
    // Allow decimal point if decimals are allowed and it doesn't already exist
    (allowDecimals && e.key === '.' && !currentValue.includes('.'))
  ) {
    return;
  }

  // Allow numbers 0-9
  if (!/[0-9]/.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Validates pasted content for numeric fields
 * Returns true if valid, otherwise prevents default and returns false
 */
export const validateNumericPaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
  allowDecimals: boolean = true,
): boolean => {
  const pastedText = e.clipboardData.getData('text');

  // Check if the pasted content is a valid number
  const regex = allowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
  const isValid = regex.test(pastedText);

  if (!isValid) {
    e.preventDefault();
    return false;
  }

  return true;
};

/**
 * Converts a string input to a numeric value and rounds to specified decimals
 * Returns null for empty or invalid inputs
 */
export const parseAndRoundNumericInput = (
  input: string,
  decimals: number = 2,
): number | null => {
  if (input === '' || input === '.') return null;

  const numericValue = input.replace(/[^\d.]/g, '');
  const parsedValue = parseFloat(numericValue);

  if (isNaN(parsedValue)) return null;

  // Round to specified decimal places
  return parseFloat(parsedValue.toFixed(decimals));
};

/**
 * Returns a value if it's of the expected type, otherwise computes a fallback value.
 *
 * @template T - The expected type of the value
 * @template R - The return type, defaults to T
 *
 * @param value - The value to check
 * @param computeFn - Function to compute a fallback value
 * @param typeGuard - Function to determine if the value is of the expected type
 * @param fallback - Final fallback value if both value and computed result are falsy
 *
 * @returns The original value if it passes the type guard, otherwise the computed value,
 *          or the fallback if both are falsy
 *
 * @example
 * // For number values with number computation
 * const totalRooms = getValueOrComputed<number>(
 *   unit.totalRooms,
 *   () => calculateRoomCount(unit),
 *   (val): val is number => typeof val === 'number',
 *   null
 * );
 *
 * @example
 * // With different return type
 * const formattedPrice = getValueOrComputed<number, string>(
 *   product.price,
 *   () => calculatePrice(product),
 *   (val): val is number => typeof val === 'number' && !isNaN(val),
 *   '$0.00'
 * );
 */
export function getValueOrComputed<T, R = T>(
  value: unknown,
  computeFn: () => R | null | undefined,
  typeGuard: (val: unknown) => val is T,
  fallback: R | null = null,
): R | null {
  // If value is of expected type, return it (cast to R since R defaults to T)
  if (typeGuard(value)) {
    return value as unknown as R;
  }

  // Compute fallback value
  const computed = computeFn();

  // Return computed value or final fallback
  return computed || fallback;
}

/**
 * Convenience function for handling numeric values
 *
 * @param value - The numeric value to check
 * @param computeFn - Function to compute a fallback numeric value
 * @param fallback - Final fallback value if both value and computed result are falsy
 *
 * @returns The original value if it's a number, otherwise the computed value,
 *          or the fallback if both are falsy
 *
 * @example
 * const totalRooms = getNumberValueOrComputed(
 *   unit.totalRooms,
 *   () => calculateRoomCount(unit),
 *   null
 * );
 */
export function getNumberValueOrComputed<R = number>(
  value: unknown,
  computeFn: () => R | null | undefined,
  fallback: R | null = null,
): R | null {
  return getValueOrComputed<number, R>(
    value,
    computeFn,
    (val): val is number => typeof val === 'number' && !isNaN(val),
    fallback,
  );
}

/**
 * Generates a consistent data-testid attribute value for form fields
 *
 * @param field - The field object containing Label/Key or a string
 * @param fieldMeta - The field metadata containing Name
 * @param fieldType - Optional type identifier for the field
 * @returns A string to use as the data-testid
 */
export const generateDataTestId = (
  field: ControlField | string,
  fieldMeta?: FieldMetadata,
  fieldType: string = 'Field',
): string => {
  const baseId = (
    typeof field === 'string' ? field : field?.Label || fieldMeta?.Name
  )?.trim();
  return `${baseId}-${fieldType}`;
};

export const findNextPositiveNumberFromArray = (
  arrayOfNumbers: number[],
): number => {
  const sortedUniqueLevels = [...arrayOfNumbers].sort(
    (a: number, b: number) => a - b,
  );

  // Find the first missing positive integer
  let nextLevel = 1;
  for (const level of sortedUniqueLevels) {
    if (level === nextLevel) {
      nextLevel++;
    } else if (level > nextLevel) {
      // If the current level is greater than nextLevel,
      // then nextLevel is the first missing one
      return nextLevel;
    }
  }

  return nextLevel;
};

// Small helper for error messages thrown by system backend calls
export const formatSystemError = (error: any): string => {
  return (
    (error.details && error.details.length > 0
      ? `${error.details[0].target} ${error.details[0].message}`
      : error.message) || 'An unknown error occurred'
  );
};
